<template lang="pug">
.footer-static#footer
  .funneled(:class="{'hide-mobile': $route.meta.hideStaticFooter}")
    rogue-link(to="/contact-us" target='_blank')
      | {{ __('Contact Us')}}
      span.line |
    rogue-link(to="/return-policies" target='_blank')
      | {{ __('Return Policies')}}
      span.line |
    rogue-link(to="/checkout-faq" target='_blank') {{ __('Checkout FAQ')}}
  .mobile
    .d-col(v-for="(col, i) in mobileColumns" :key="i")
      .section(v-for="(links, title) in col" :key="title" @click="collapsedSections[title] = !collapsedSections[title]")
        .title-container
          h4.filter-header
            | {{ __(title) }}
          Icon.icon-size-xxs(fill="#ffffff" :name="!collapsedSections[title] ? 'arrow-down' : 'arrow-up'")
        .item( v-for="(href, text, index) in links" :key="text" v-if="collapsedSections[title]" :class="{ 'first-item': index === 0 }")
          rogue-link.item(v-if="isHref(href)" :to="href" :target="href && href.indexOf('http') === 0 ? '_blank' : ''" :aria-label=`'Link to: ' + text` @click.stop="handleLinkClick(href)" ) {{ __(text) }}
          rogue-link.item(v-else-if="typeof(href) === 'object' && href?.action === 'showOverlay'" to="#" v-bind="href" :aria-label=`'Link to: ' + text`) {{ __(text) }}
          .item(v-else v-translate="'translation'" v-html="text")
      hr
  .desktop-static
    .d-col(v-for="(col, i) in desktopColumns" :key="i")
      .section(v-for="(links, title) in col" :key="title")
        h5 {{ __(title) }}
        .item(v-for="(href, text) in links" :key="text")
          rogue-link.item(v-if="isHref(href)" :to="href" :target="href && href.indexOf('http') === 0 ? '_blank' : ''" :aria-label=`'Link to: ' + text` @click.stop="scrollToHeader(href)") {{ __(text) }}
          rogue-link.item(v-else-if="typeof(href) === 'object' && href?.action === 'showOverlay'" to="#" v-bind="href" :aria-label=`'Link to: ' + text`) {{ __(text) }}
          .item(v-else v-translate="'translation'" v-html="text")
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import footerData from './footer-data.json'
import { useRogue } from '@/js/services/Rogue'

const route = useRoute()

const rogue = useRogue()

type footerDataType = {
  mobile: {
    columns: object[]
  }
  desktop: {
    columns: object[]
  }
}

const defaultData: footerDataType = { mobile: { columns: [] }, desktop: { columns: [] } }

const data = ref<footerDataType>(footerData?.[rogue.site.code] || defaultData)

const mobileColumns = computed(() => data.value.mobile?.columns || [])

const desktopColumns = computed(() => data.value.desktop?.columns || [])

const collapsedSections = ref<Record<string, boolean>>({})


function isHref(value: string) {
  const linkStarts = ['/', 'https://', 'mailto:', 'tel:']

  return value && typeof (value) !== 'object' && linkStarts.some(s => value.startsWith(s))
}

function scrollToHeader(href: string) {
  const currentRoot = route.path.split('/').filter(Boolean)[0]
  const linkRoot = href.split('/').filter(Boolean)[0]

  if (currentRoot === linkRoot) {
    const header = document.querySelector('header')

    if (header) {
      header.scrollIntoView()
    }
  }
}

function handleLinkClick(href: string) {
  scrollToHeader(href)

  Object.keys(collapsedSections.value).forEach((key) => {
    collapsedSections.value[key] = false
  })
}

</script>
<style lang="scss" scoped>
.footer-static {
  position: relative;
  flex-grow: 1;  
}

.tablet .footer-static .mobile {
  .d-col{
    margin-left: -10px;
    margin-right: -10px;
  }
  .section{
    padding: 16px 68px;
  }
}

.footer-static .mobile {
  margin-left: -56px;
  margin-right: -56px;
  margin-top: -$space-l;
  margin-bottom: $size-xxl;
  padding: 0;

  .d-col {
    margin: 0;

    hr{
      margin: 0;
    }
  }
  .section{
    padding: 16px 58px;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    cursor: pointer;

    .title-container{
      width: 100%;
      display: flex;
      justify-content: space-between;
    }

    .item{
      font-size: $space-xs;
      text-decoration: none;

      .item{
        line-height: 1.5;
      }
    }

    >.item {
      margin-top: $space-s;

      &:last-child {
        margin-bottom: 10px;
      }
    }

  h4 {
      font-size: $size-xs;
      font-family: $font-condensed;
      font-weight: bold;
      text-transform: uppercase;
      line-height: $size-xs;
      margin-bottom: 0;
    }

    .first-item{
      margin-top: $space-l;
    }
  }
}

.desktop-static {
  display: flex;

  .d-col {
    width: 25%
  }

  h5 {
    text-transform: uppercase;
  }

  .item {
    display: block;
    color: $color-gray-dark;
    margin-bottom: $space-s;
    font-size: $size-xxs;

    &:hover {
      color: $color-white;
    }
  }
}

.contact-address{
  margin-bottom: 0;
}

a {
  color: $color-white;
  font-size: $size-xxs;
  text-decoration: none;

  &:hover {
    color: $color-gray;
  }

  .line {
    margin: 0 $space-xxs;
    pointer-events: none;
  }
}

.funneled {
  text-align: center;
  display: none;
  margin-bottom: $space-m;

  @include all-desktop() {
    text-align: left;

    a {
      font-size: $size-xs;
    }
  }
}

.mobile .m-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  text-align: center;

  a {
    margin-bottom: $space-s;
  }
}

.footer-static {
  #app.funneled & {

    .mobile,
    .desktop-static {
      display: none;
    }

    @include not-desktop() {
      .funneled:not(.hide-mobile) {
        display: block;
      }
    }

    @include all-desktop() {
      .funneled {
        display: block;
      }
    }
  }
}

@include all-desktop() {
  .mobile {
    display: none;
  }

  .funneled {
    margin-bottom: 0;
  }
}

@include not-desktop() {
  .desktop-static {
    display: none;
  }
}
</style>
